import { reserveStockPatch } from '../utils/productVariants';
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  writeBatch,
  increment,
  serverTimestamp,
  addDoc,
  runTransaction,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertTriangle,
  FiZoomIn,
} from "react-icons/fi";

/* ───────────────────────── constants / helpers ───────────────────────── */
const LEVEL_RATES = [0.4, 0.2, 0.1]; // Buyer commission split L1/L2/L3
const MIRROR_REFERRER_NOTIF_TO_USERS = true;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const clamp0 = (n) => Math.max(0, round2(n));

const UI_CURRENCY = "LKR";
function fmt(_curIgnored, n) {
  return `${UI_CURRENCY} ${Number(n || 0).toLocaleString()}`;
}

function makeNotif({
  title,
  body,
  type = "info",
  link = "",
  cta = "",
  extra = {},
}) {
  return {
    title,
    body,
    message: body,
    text: body,
    description: body,
    type,
    link,
    cta,
    read: false,
    seen: false,
    createdAt: serverTimestamp(),
    ...extra,
  };
}
function setAllNotifTargets(batch, uid, payload, opts = {}) {
  const { toUsers = true, toReferralUsers = false } = opts;
  batch.set(doc(collection(db, "notifications")), { userId: uid, ...payload });
  if (toUsers)
    batch.set(doc(collection(db, "users", uid, "notifications")), payload);
  if (toReferralUsers)
    batch.set(
      doc(collection(db, "referral_users", uid, "notifications")),
      payload,
    );
}

/* ───────────────────────── referral lookups (same as course page) ───────────────────────── */
async function findReferralUserByUid(uid) {
  if (!uid) return null;
  const qs = await getDocs(
    query(collection(db, "referral_users"), where("uid", "==", uid), limit(1)),
  );
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}
async function findReferralUserByCode(code) {
  if (!code) return null;
  const qs = await getDocs(
    query(
      collection(db, "referral_users"),
      where("referralCode", "==", code),
      limit(1),
    ),
  );
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}
async function findReferralCodeMapping(code) {
  if (!code) return null;
  const direct = await getDoc(doc(db, "referral_codes", code));
  if (direct.exists()) return { id: direct.id, ...direct.data() };
  const qs = await getDocs(
    query(
      collection(db, "referral_codes"),
      where("code", "==", code),
      limit(1),
    ),
  );
  if (!qs.empty) {
    const d = qs.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}
async function findUserByCode(code) {
  if (!code) return null;
  const candidates = ["referralCode", "sponsorCode", "referredBy"];
  for (const field of candidates) {
    const qs = await getDocs(
      query(collection(db, "users"), where(field, "==", code), limit(1)),
    );
    if (!qs.empty) {
      const d = qs.docs[0];
      return { id: d.id, ...d.data() };
    }
  }
  return null;
}
async function getUidForReferralCode(codeRaw) {
  const code = (codeRaw || "").trim();
  if (!code) return null;
  const ru = await findReferralUserByCode(code);
  if (ru?.uid) return ru.uid;
  const rc = await findReferralCodeMapping(code);
  const rcUid = rc?.ownerUserId || rc?.userId || rc?.uid || null;
  if (rcUid) return rcUid;
  const u = await findUserByCode(code);
  if (u?.id) return u.id;
  return null;
}
async function getSponsor(uid) {
  try {
    if (!uid) return null;

    // try referral_users
    const childRU = await findReferralUserByUid(uid);
    if (childRU) {
      const sponsorCode = (
        childRU.referredBy ||
        childRU.sponsorCode ||
        childRU.parentCode ||
        ""
      ).trim();
      if (sponsorCode) {
        const sponsorUid = await getUidForReferralCode(sponsorCode);
        if (sponsorUid && sponsorUid !== uid) return sponsorUid;
      }
    }

    // fallback to users doc
    const us = await getDoc(doc(db, "users", uid));
    if (us.exists()) {
      const u = us.data() || {};
      const sponsorUid =
        u.sponsorId || u.referrerUserId || u.referredByUid || null;
      if (sponsorUid && sponsorUid !== uid) return sponsorUid;

      const sponsorCode2 = (
        u.referredBy ||
        u.sponsorCode ||
        u.referralCode ||
        ""
      ).trim();
      if (sponsorCode2) {
        const sponsorUid2 = await getUidForReferralCode(sponsorCode2);
        if (sponsorUid2 && sponsorUid2 !== uid) return sponsorUid2;
      }
    }

    return null;
  } catch (e) {
    console.error("[getSponsor] failed for uid:", uid, e);
    return null;
  }
}

/* ───────────────────────── Drive + image helpers (match course page behavior) ───────────────────────── */
function extractDriveId(rawUrl) {
  const url = (rawUrl || "").trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return m[1];
    const q = u.searchParams.get("id");
    if (q) return q;
  } catch {}
  return null;
}
function buildDriveImageCandidates(thumbnailUrl) {
  const id = extractDriveId(thumbnailUrl);
  if (!id) return [thumbnailUrl || "/placeholder.png"];
  return [
    `https://drive.usercontent.google.com/uc?id=${id}&export=view`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    `https://drive.google.com/uc?export=download&id=${id}`,
    `https://lh3.googleusercontent.com/d/${id}=w1600-h900-no`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w1600`,
  ];
}
const PLACEHOLDER = "/placeholder.png";
function buildCoverCandidates(p = {}) {
  const set = new Set();
  const add = (v) => typeof v === "string" && v.trim() && set.add(v.trim());

  add(p.thumbnailUrl);
  add(p.imageUrl);
  add(p.coverUrl);
  add(p.image);
  add(p.cover);
  add(p.photo);

  // arrays
  ["images", "gallery", "photos", "media", "pictures", "pics"].forEach((k) => {
    const arr = p[k];
    if (!Array.isArray(arr)) return;
    arr.forEach((it) => {
      if (typeof it === "string") add(it);
      else if (it && typeof it === "object")
        add(it.url || it.src || it.href || it.link);
    });
  });

  const chain = [];
  for (const u of set) {
    const isDrive = !!extractDriveId(u);
    if (!isDrive) chain.push(u);
    buildDriveImageCandidates(u).forEach((c) => chain.push(c));
  }
  if (chain.length === 0) chain.push(PLACEHOLDER);
  return [...new Set(chain)];
}

function ImageChain({ sources = [PLACEHOLDER], alt = "" }) {
  const [idx, setIdx] = useState(0);
  const [src, setSrc] = useState(sources[0] || PLACEHOLDER);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setIdx(0);
    setSrc(sources[0] || PLACEHOLDER);
    setLoaded(false);
  }, [sources]);

  useEffect(() => {
    setLoaded(false);
    if (!sources.length) return;
    let alive = true;
    const candidate = sources[idx] || PLACEHOLDER;
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = candidate;
    img.onload = () => alive && (setSrc(candidate), setLoaded(true));
    img.onerror = () => {
      if (!alive) return;
      if (idx < sources.length - 1) setIdx((n) => n + 1);
      else setLoaded(true);
    };
    return () => (alive = false);
  }, [idx, sources]);

  return (
    <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 w-14 h-14">
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-100" />
      )}
      <img
        src={src}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        loading="eager"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

/* ---------------- UI bits ---------------- */
function Status({ value }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    paid: "bg-blue-100 text-blue-700",
    processing: "bg-indigo-100 text-indigo-700",
    shipped: "bg-cyan-100 text-cyan-700",
    completed: "bg-green-100 text-green-700",
    declined: "bg-rose-100 text-rose-700",
  };
  const tone = map[value] || "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`}
    >
      {value === "pending" ? (
        <FiClock />
      ) : value === "declined" ? (
        <FiXCircle />
      ) : (
        <FiCheckCircle />
      )}
      {value || "unknown"}
    </span>
  );
}
function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
      <div className={`bg-white rounded-2xl shadow-xl border w-full ${max}`}>
        <div className="p-5">{children}</div>
        <div className="p-3 text-center">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border hover:bg-gray-50 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page helpers ---------------- */
function isProductOrder(o = {}) {
  const p = o.product || {};
  return !!(p.id || p.price != null || p.title || p.isPhysical != null);
}

function getWholesaleOrderMeta(order = {}) {
  const mode = String(
    order?.pricingMode || order?.product?.pricingMode || "retail",
  ).toLowerCase();
  const qualified =
    order?.wholesaleQualified === true || order?.product?.isWholesale === true;
  const minQty = Number(
    order?.wholesaleMinQty || order?.product?.wholesaleMinQty || 0,
  );
  const retailUnitPrice = Number(
    order?.retailUnitPrice || order?.product?.retailUnitPrice || 0,
  );
  const wholesaleUnitPrice =
    order?.wholesaleUnitPrice != null && order?.wholesaleUnitPrice !== ""
      ? Number(order.wholesaleUnitPrice)
      : order?.product?.wholesaleUnitPrice != null &&
          order?.product?.wholesaleUnitPrice !== ""
        ? Number(order.product.wholesaleUnitPrice)
        : null;
  return {
    mode,
    qualified,
    minQty,
    retailUnitPrice,
    wholesaleUnitPrice,
    isWholesale: mode === "wholesale" || mode === "mixed_wholesale" || qualified,
  };
}

function getOrderedProductLines(order = {}) {
  const source =
    Array.isArray(order.productItems) && order.productItems.length > 0
      ? order.productItems
      : order?.product?.id || order?.productId
        ? [
            {
              id: order?.product?.id || order?.productId || "",
              title: order?.product?.title || "",
              qty: Number(order?.product?.qty || 1),
            },
          ]
        : [];

  const merged = new Map();
  source.forEach((item) => {
    const id = String(item?.id || item?.productId || "").trim();
    if (!id) return;
    const qty = Math.max(1, Number(item?.qty || item?.quantity || 1) || 1);
    const key = `${id}::${item.variantId || ""}`;
    const prev = merged.get(key) || { id, title: item?.title || "", qty: 0 };
    merged.set(key, {
      variantId: item.variantId || "",
      id,
      title: prev.title || item?.title || "",
      qty: prev.qty + qty,
    });
  });
  return Array.from(merged.values());
}

async function reserveApprovedOrderStock(order = {}) {
  const lines = getOrderedProductLines(order);
  if (!lines.length) return;
  await runTransaction(db, async transaction => {
    const orderRef=doc(db,'orders',order.id);
    const orderSnapshot=await transaction.get(orderRef);
    if(!orderSnapshot.exists())throw new Error('Order is no longer available.');
    const fresh=orderSnapshot.data();
    if(fresh.payment?.stockReserved || fresh.stockReservationStatus==='reserved')return;
    if(fresh.status!=='pending')throw new Error('Only pending orders can reserve stock.');
    const lines = getOrderedProductLines(fresh);
    const ids=[...new Set(lines.map(line=>line.id))];
    // Read every document before writing; multiple variants share one product.
    const products=await Promise.all(ids.map(id=>transaction.get(doc(db,'courses',id))));
    const patches=products.map((snapshot,index)=>{
      if(!snapshot.exists())throw new Error('An ordered product is no longer available.');
      return {ref:doc(db,'courses',ids[index]),patch:reserveStockPatch(snapshot.data(),lines.filter(line=>line.id===ids[index]))};
    });
    for(const {ref,patch} of patches) if(Object.keys(patch).length)transaction.set(ref,{...patch,updatedAt:serverTimestamp()},{merge:true});
    transaction.update(orderRef,{payment:{...(fresh.payment||{}),stockReserved:true}});
  });
}

/**
 * Product split rules (mirrors course split):
 * - endPrice: product.price (preferred) or stored order amount
 * - commission/company/service/others: taken from product doc if present
 * - instructor (seller) payout = product.price (net). If missing, fallback to derived gross.
 * - sellerUid resolves from product doc typical fields.
 */
async function getProductSplit(productId, orderAmount, order = {}) {
  if (!productId) {
    const endPrice = Number(orderAmount || 0);
    return {
      endPrice,
      commission: 0,
      company: 0,
      service: 0,
      others: 0,
      instructor: 0,
      instructorUid: null,
      source: "fallback",
      productDoc: {},
    };
  }

  let snap = await getDoc(doc(db, "courses", productId));
  if (!snap.exists()) {
    snap = await getDoc(doc(db, "products", productId));
  }
  if (!snap.exists()) {
    const endPrice = Number(orderAmount || 0);
    return {
      endPrice,
      commission: 0,
      company: 0,
      service: 0,
      others: 0,
      instructor: 0,
      instructorUid: null,
      source: "fallback",
      productDoc: {},
    };
  }

  const p = snap.data() || {};
  const endPrice = Number(order.productItems?.some(item=>item.variantId) ? orderAmount : p.price ?? p.finalPrice ?? orderAmount ?? 0) || 0;

  const commission = clamp0(
    Number(
      order?.affiliateTotalCommissionAmount ??
        order?.affiliateCommissionAmount ??
        p.affiliateTotalCommissionAmount ??
        p.affiliateCommissionAmount ??
        p.commission ??
        0,
    ),
  );
  const company = clamp0(Number(p.company || 0));
  const service = clamp0(Number(p.service || 0));
  const others = clamp0(Number(p.others || 0));

  const instructorNet = clamp0(
    endPrice - (commission + company + service + others),
  );

  const instructorUid =
    p.sellerUserId ||
    p.instructorUserId ||
    p.ownerUid ||
    p.ownerId ||
    p.createdBy ||
    p.userId ||
    null;

  return {
    endPrice,
    commission,
    company,
    service,
    others,
    instructor: instructorNet,
    instructorUid,
    source: "entered_price_less_commission",
    productDoc: p,
  };
}

/* ---------------- Component ---------------- */
export default function ProductOrderReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [statusFilter, setStatusFilter] = useState("pending");
  const [onlyProducts, setOnlyProducts] = useState(true);
  const [hideGhosts, setHideGhosts] = useState(true);
  const [search, setSearch] = useState("");

  // modals
  const [slipUrl, setSlipUrl] = useState("");
  const [confirmApprove, setConfirmApprove] = useState({ open: false, id: "" });
  const [decline, setDecline] = useState({ open: false, id: "", reason: "" });

  const adminUid = getAuth().currentUser?.uid || "admin";

  // live load
  useEffect(() => {
    setLoading(true);
    let qRef;
    if (statusFilter === "all") {
      qRef = query(
        collection(db, "orders"),
        orderBy("createdAt", "desc"),
        limit(300),
      );
    } else {
      qRef = query(
        collection(db, "orders"),
        where("status", "==", statusFilter),
        orderBy("createdAt", "desc"),
        limit(300),
      );
    }
    const unsub = onSnapshot(qRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setRows(list);
      setLoading(false);
    });
    return () => unsub();
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = rows;
    if (onlyProducts) list = list.filter(isProductOrder);
    if (hideGhosts)
      list = list.filter(
        (o) => Number(o?.amountDetail?.total || 0) > 0 || !!o?.payment?.slipUrl,
      );
    if (!s) return list;
    return list.filter((o) =>
      [o.id, o.product?.title, o.buyer?.name, o.buyer?.phone, o.buyer?.email]
        .filter(Boolean)
        .some((t) => String(t).toLowerCase().includes(s)),
    );
  }, [rows, search, onlyProducts, hideGhosts]);

  const wholesaleStats = useMemo(() => {
    return filtered.reduce(
      (acc, order) => {
        const meta = getWholesaleOrderMeta(order);
        const total = Number(order?.amountDetail?.total || order?.amount || 0);
        if (meta.isWholesale) {
          acc.wholesaleOrders += 1;
          acc.wholesaleRevenue += total;
        } else {
          acc.retailOrders += 1;
          acc.retailRevenue += total;
        }
        return acc;
      },
      {
        retailOrders: 0,
        wholesaleOrders: 0,
        retailRevenue: 0,
        wholesaleRevenue: 0,
      },
    );
  }, [filtered]);

  /* --- shared tiny buyer notifier (kept for table-level quick messages) --- */
  async function notifyBuyerSimple(o, payload) {
    try {
      if (!o.buyerUid) return;
      await addDoc(collection(db, "notifications"), {
        uid: o.buyerUid,
        type: "order_status",
        orderId: o.id,
        status: payload.status,
        title: payload.title,
        body: payload.body,
        createdAt: serverTimestamp(),
        read: false,
      });
    } catch (e) {
      console.error("notifyBuyer failed", e);
    }
  }

  /* ───────────────────────── main APPROVE (mirrors course approve) ───────────────────────── */
  async function doApprove() {
    const id = confirmApprove.id;
    if (!id) return;
    try {
      const orderRef = doc(db, "orders", id);
      const o =
        rows.find((r) => r.id === id) ||
        (await (await getDoc(orderRef)).data());

      if (!o) throw new Error("Order not found");
      if (String(o.status || "").toLowerCase() !== "pending") {
        throw new Error("Only pending orders can be approved.");
      }
      const productId = o?.product?.id || o?.productId;
      const amountPaid = Number(o?.amountDetail?.total ?? o?.amount ?? 0) || 0;
      const currency = o?.amountDetail?.currency || o?.currency || "LKR";

      // split
      const split = await getProductSplit(productId, amountPaid, o);
      const {
        endPrice,
        commission,
        company,
        service,
        others,
        instructor: instructorBase,
        instructorUid,
      } = split;

      await reserveApprovedOrderStock({...o,id});

      // buyer commission chain (40/20/10 of commission pool)
      let L1 =
        (o.referrerUserId || o.refAttribution?.refUid || "").trim() || null;
      if (!L1) {
        const codeRaw = (o.referralCode || o.refAttribution?.code || "").trim();
        if (codeRaw) L1 = await getUidForReferralCode(codeRaw);
      }
      if (!L1) {
        const fallback = await getSponsor(o.buyerUid || o.userId);
        if (fallback) L1 = fallback;
      }
      const L2 = await getSponsor(L1);
      const L3 = await getSponsor(L2);
      const chain = [L1, L2, L3];

      const commissionBase = clamp0(commission);
      const shares = LEVEL_RATES.map((r) => round2(commissionBase * r));
      let commissionPoolAmount = 0;
      const dist = [];
      chain.forEach((uid, i) => {
        const amount = shares[i];
        if (uid) dist.push({ userId: uid, level: i + 1, amount });
        else commissionPoolAmount += amount;
      });
      const allocated = round2(dist.reduce((t, d) => t + d.amount, 0));
      const remainder = round2(
        commissionBase - allocated - round2(commissionPoolAmount),
      );
      commissionPoolAmount = round2(commissionPoolAmount + remainder);

      // writes
      const batch = writeBatch(db);

      // order -> paid + accounting fields
      batch.update(orderRef, {
        status: "paid",
        adminApprovedAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        adminApprovedBy: adminUid,
        "payment.reviewed": true,
        "payment.declineReason": null,

        endPrice,
        commissionAmount: commissionBase,
        commissionDistribution: dist,
        commissionPoolAmount,
        instructorAmount: instructorBase,
        companyAmount: company,
        serviceAmount: service,
        othersAmount: others,
        instructorUserId: instructorUid || null,
        uplineChain: [
          { level: 1, uid: L1 || null },
          { level: 2, uid: L2 || null },
          { level: 3, uid: L3 || null },
        ],

        instructorReferralUpliftTotal: 0,
        instructorReferralDistribution: [],
        instructorReferralPoolAmount: 0,
        instructorUplineChain: [],
        amount: amountPaid, // <-- normalize for Daily Sales sums
        courseTitle: o.product?.title || null, // <-- optional: so the table shows a name
        courseId: productId || null,
      });

      // payouts + wallets + notifications (buyer side)
      for (const d of dist) {
        const payoutRef = doc(collection(db, "payouts"));
        batch.set(payoutRef, {
          userId: d.userId,
          orderId: id,
          productId,
          productTitle: o.product?.title || "",
          amount: d.amount,
          level: d.level,
          currency,
          status: "pending",
          createdAt: serverTimestamp(),
          source: "commission",
        });
        batch.set(
          doc(db, "users", d.userId),
          {
            walletBalance: increment(d.amount),
            totalCommission: increment(d.amount),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        const refPayload = makeNotif({
          title: "Commission earned 🎉",
          body: `One of your referrals purchased “${
            o.product?.title || "a product"
          }”. You earned ${fmt(currency, d.amount)} (Level ${d.level}).`,
          type: "commission",
          extra: { orderId: id, productId, level: d.level, amount: d.amount },
        });
        setAllNotifTargets(batch, d.userId, refPayload, {
          toUsers: MIRROR_REFERRER_NOTIF_TO_USERS,
          toReferralUsers: true,
        });
      }

      // seller payout (FULL product.price)
      if (instructorUid && instructorBase > 0) {
        const payoutRef = doc(collection(db, "payouts"));
        batch.set(payoutRef, {
          userId: instructorUid,
          orderId: id,
          productId,
          productTitle: o.product?.title || "",
          amount: instructorBase,
          level: 0,
          role: "seller",
          currency,
          status: "pending",
          createdAt: serverTimestamp(),
          source: "instructor_revenue",
        });
        batch.set(
          doc(db, "users", instructorUid),
          {
            walletBalance: increment(instructorBase),
            instructorEarnings: increment(instructorBase),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        const instPayload = makeNotif({
          title: "New product revenue 💼",
          body: `Your product “${o.product?.title || "a product"}” sold. ${fmt(
            currency,
            instructorBase,
          )} has been credited to your wallet.`,
          type: "payout",
          extra: { orderId: id, productId, amount: instructorBase },
        });
        setAllNotifTargets(batch, instructorUid, instPayload);
      }

      // pools
      if (split.company > 0)
        batch.set(
          doc(db, "pools", "company"),
          { balance: increment(split.company), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (split.service > 0)
        batch.set(
          doc(db, "pools", "service"),
          { balance: increment(split.service), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (split.others > 0)
        batch.set(
          doc(db, "pools", "others"),
          { balance: increment(split.others), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (commissionPoolAmount > 0)
        batch.set(
          doc(db, "pools", "commission"),
          {
            balance: increment(commissionPoolAmount),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      // buyer notification
      const buyerPayload = makeNotif({
        title: "Payment approved ✅",
        body: `Your payment for “${
          o.product?.title || "a product"
        }” is approved. We’ll start processing your order.`,
        type: "success",
        extra: { orderId: id, productId, amount: amountPaid },
      });
      if (o.buyerUid) setAllNotifTargets(batch, o.buyerUid, buyerPayload);

      await batch.commit();

      // keep your simple notifier too (optional duplicate)
      await notifyBuyerSimple(o || { id, buyerUid: null }, {
        status: "paid",
        title: "Payment approved",
        body: "Your payment slip was verified. We’ll start processing your order shortly.",
      });

      setConfirmApprove({ open: false, id: "" });
      alert(
        "Approved: payouts distributed, pools credited, and notifications sent.",
      );
    } catch (e) {
      console.error("Approve failed:", e);
      alert(`Approve failed: ${e.message}`);
    }
  }

  async function doDecline() {
    const { id, reason } = decline;
    if (!id || !reason.trim()) return;
    try {
      const ref = doc(db, "orders", id);
      const o = rows.find((r) => r.id === id);
      await updateDoc(ref, {
        status: "declined",
        adminDeclinedAt: serverTimestamp(),
        adminDeclinedBy: adminUid,
        "payment.reviewed": true,
        "payment.declineReason": reason.trim(),
      });

      // notify buyer (both ways to match your style)
      const payload = {
        status: "declined",
        title: "Payment declined",
        body: `Reason: ${reason.trim()}`,
      };
      await notifyBuyerSimple(o || { id, buyerUid: null }, payload);

      if (o?.buyerUid) {
        const batch = writeBatch(db);
        const notif = makeNotif({
          title: "Payment declined",
          body: `Your payment for “${
            o.product?.title || "a product"
          }” was declined. Reason: ${reason.trim()}.`,
          type: "error",
          extra: {
            orderId: id,
            productId: o?.product?.id || o?.productId || "",
          },
        });
        setAllNotifTargets(batch, o.buyerUid, notif);
        await batch.commit();
      }
    } catch (e) {
      console.error("Decline failed:", e);
      alert(`Decline failed: ${e.message}`);
    }
    setDecline({ open: false, id: "", reason: "" });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f8fa] via-[#eef3ff] to-[#f6f2ff]">
      <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        <div className="rounded-3xl bg-white border shadow-sm p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl md:text-2xl font-black tracking-tight">
              Orders Review
            </h1>

            <div className="flex items-center gap-2">
              <select
                className="px-3 py-2 rounded-xl border text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="declined">Declined</option>
                <option value="all">All (recent)</option>
              </select>

              <label className="text-sm text-gray-600 inline-flex items-center gap-2 px-3 py-2 rounded-xl border">
                <input
                  type="checkbox"
                  checked={onlyProducts}
                  onChange={(e) => setOnlyProducts(e.target.checked)}
                />
                Only products
              </label>

              <label className="text-sm text-gray-600 inline-flex items-center gap-2 px-3 py-2 rounded-xl border">
                <input
                  type="checkbox"
                  checked={hideGhosts}
                  onChange={(e) => setHideGhosts(e.target.checked)}
                />
                Hide zero/blank
              </label>

              <div className="relative">
                <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by order ID, buyer, phone, product…"
                  className="pl-9 pr-3 py-2 rounded-xl border text-sm w-72"
                />
              </div>
            </div>
          </div>

          {/* Table */}
        <div className="mt-5 overflow-x-auto">
          <div className="mb-5 grid gap-4 md:grid-cols-4">
            <div className="rounded-2xl border bg-white px-4 py-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                Retail Orders
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {wholesaleStats.retailOrders}
              </div>
            </div>
            <div className="rounded-2xl border bg-white px-4 py-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                Wholesale Orders
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {wholesaleStats.wholesaleOrders}
              </div>
            </div>
            <div className="rounded-2xl border bg-white px-4 py-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                Retail Revenue
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {fmt(UI_CURRENCY, wholesaleStats.retailRevenue)}
              </div>
            </div>
            <div className="rounded-2xl border bg-white px-4 py-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                Wholesale Revenue
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {fmt(UI_CURRENCY, wholesaleStats.wholesaleRevenue)}
              </div>
            </div>
          </div>

          <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left py-2">Order</th>
                  <th className="text-left py-2">Buyer</th>
                  <th className="text-left py-2">Mode</th>
                  <th className="text-left py-2">Slip</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-400">
                      No orders
                    </td>
                  </tr>
                )}

                {filtered.map((o) => {
                  const status = o.status || "pending";
                  const p = o.product || {};
                  const covers = buildCoverCandidates(p);
                  const wholesale = getWholesaleOrderMeta(o);

                  return (
                    <tr key={o.id} className="align-top">
                      <td className="py-3 pr-4">
                        <div className="flex items-start gap-3">
                          <ImageChain sources={covers} alt={p.title} />
                          <div>
                            <div className="font-semibold line-clamp-1">
                              {p.title || "Product"}
                            </div>
                            <div className="text-xs text-gray-400">#{o.id}</div>
                            <div className="text-[11px] text-gray-400">
                              Qty {p.qty || 1} ·{" "}
                              {p.isPhysical ? "Physical" : "Digital"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 pr-4">
                        <div className="font-medium">
                          {o.buyer?.name || "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {o.buyer?.phone || "-"}
                        </div>
                        <div className="text-xs text-gray-400">
                          {o.buyer?.email || ""}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {[o.buyer?.address1, o.buyer?.city]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </td>

                      <td className="py-3 pr-4">
                        <div className="flex flex-col gap-2">
                          <span
                            className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                              wholesale.isWholesale
                                ? "bg-[#f3ebff] text-[#6a4f93]"
                                : "bg-[#fff3df] text-[#8a5a1d]"
                            }`}
                          >
                            {wholesale.isWholesale ? "Wholesale" : "Retail"}
                          </span>
                          {wholesale.isWholesale ? (
                            <div className="text-[11px] leading-5 text-gray-500">
                              {wholesale.mode === "mixed_wholesale"
                                ? "Mixed wholesale cart"
                                : wholesale.qualified
                                  ? "Qualified"
                                  : "Selected but not qualified"}{" "}
                              {wholesale.minQty > 0
                                ? `· Min ${wholesale.minQty}`
                                : ""}
                              {wholesale.wholesaleUnitPrice != null
                                ? ` · ${fmt(UI_CURRENCY, wholesale.wholesaleUnitPrice)} / unit`
                                : ""}
                            </div>
                          ) : (
                            <div className="text-[11px] leading-5 text-gray-500">
                              {wholesale.retailUnitPrice > 0
                                ? `${fmt(UI_CURRENCY, wholesale.retailUnitPrice)} / unit`
                                : "Standard retail pricing"}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-3 pr-4">
                        {o.payment?.slipUrl ? (
                          <button
                            onClick={() => setSlipUrl(o.payment.slipUrl)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-gray-50"
                          >
                            <FiZoomIn /> View slip
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">No slip</span>
                        )}
                        {o.payment?.declineReason && (
                          <div className="mt-2 text[11px] text-rose-600 flex items-center gap-1">
                            <FiAlertTriangle /> {o.payment.declineReason}
                          </div>
                        )}
                      </td>

                      <td className="py-3 pl-4 text-right font-extrabold">
                        LKR {(o?.amountDetail?.total || 0).toLocaleString()}
                      </td>

                      <td className="py-3 pr-4">
                        <Status value={status} />
                      </td>

                      <td className="py-3 pl-4 text-right">
                        <div className="rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-700">
                          Product payment review is now handled by the seller in
                          the instructor portal.
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Slip modal */}
      <Modal open={!!slipUrl} onClose={() => setSlipUrl("")} max="max-w-4xl">
        <div className="text-lg font-bold mb-3">Payment slip</div>
        {slipUrl?.toLowerCase().includes(".pdf") ? (
          <a
            href={slipUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            Open PDF in new tab
          </a>
        ) : (
          <img
            src={slipUrl}
            alt="slip"
            className="max-h-[70vh] mx-auto rounded-xl border"
          />
        )}
      </Modal>

      {/* Approve confirm modal */}
      <Modal
        open={confirmApprove.open}
        onClose={() => setConfirmApprove({ open: false, id: "" })}
      >
        <div className="text-lg font-bold">Approve order?</div>
        <p className="text-sm text-gray-600 mt-1">
          This will mark the payment as <b>approved</b> and set status to{" "}
          <b>paid</b>. Commissions, seller revenue, and pools will be credited
          automatically and the buyer will be notified.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setConfirmApprove({ open: false, id: "" })}
            className="px-3 py-2 rounded-xl border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={doApprove}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
          >
            Approve
          </button>
        </div>
      </Modal>

      {/* Decline modal with reason */}
      <Modal
        open={decline.open}
        onClose={() => setDecline({ open: false, id: "", reason: "" })}
      >
        <div className="text-lg font-bold">Decline order</div>
        <p className="text-sm text-gray-600 mt-1">
          Provide a reason. The status will be set to <b>declined</b>, and the
          buyer will be notified.
        </p>
        <textarea
          value={decline.reason}
          onChange={(e) =>
            setDecline((s) => ({ ...s, reason: e.target.value }))
          }
          rows={4}
          placeholder="Reason (e.g., amount mismatch / unreadable slip / reference not found)…"
          className="mt-3 w-full rounded-xl border p-2 text-sm"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            onClick={() => setDecline({ open: false, id: "", reason: "" })}
            className="px-3 py-2 rounded-xl border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={!decline.reason.trim()}
            onClick={doDecline}
            className={`px-3 py-2 rounded-xl text-white font-semibold ${
              decline.reason.trim()
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-rose-300"
            }`}
          >
            Decline
          </button>
        </div>
      </Modal>
    </div>
  );
}
